import React, { useEffect, useMemo, useState } from 'react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { CountBand } from '../components/ui/CountBand';
import { apiList } from '../api/apiClient';
import { useAuth } from '../hooks/useAuth';
import { isAdmin } from '../utils/roles';

/** V4: Visitor phone PII — masked for non-admin (PM/dealer/customer). */
const maskVisitorPhone = (raw: string): string => {
  if (!raw) return raw;
  const digits = String(raw).replace(/\D/g, '');
  if (digits.length < 7) return '•••';
  return `•••-•••-${digits.slice(-4)}`;
};

/**
 * Simple custom report builder.
 *
 * Pick a source (Call Events or Device Events), narrow with a handful of
 * filters, hit "Run Report", review the table preview, then export to CSV
 * or PDF. CSV is a blob download; PDF uses the browser's native print
 * pipeline with a print-only stylesheet so we don't pull in a PDF lib.
 *
 * Sources:
 *   - Call Events  → GET /events/calls?property_id=…
 *   - Device Events → GET /acs-systems/unlock-events?property_id=…
 *
 * Filters apply client-side after the fetch so the user can iterate
 * without re-hitting the API every time.
 */

type Source = 'calls' | 'devices';

interface Property {
  id: string | number;
  uuid?: string;
  name: string;
}

const fmtDateTime = (iso?: string | null) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(); } catch { return String(iso); }
};

const fmtDuration = (secs?: number | null) => {
  const s = Number(secs || 0);
  if (!s) return '';
  const m = Math.floor(s / 60);
  const r = s % 60;
  return m ? `${m}m ${r}s` : `${r}s`;
};

// Field catalogue per source. Drives column visibility + CSV headers.
interface FieldDef {
  key: string;
  label: string;
  /** Render for the on-screen table. */
  render?: (row: any) => React.ReactNode;
  /** Render for CSV / PDF (always a string). */
  toText?: (row: any) => string;
}

const CALL_FIELDS: FieldDef[] = [
  { key: 'created_at',    label: 'When',        render: r => fmtDateTime(r.created_at), toText: r => fmtDateTime(r.created_at) },
  { key: 'property_name', label: 'Property',    render: r => r.property_name || '—', toText: r => r.property_name || '' },
  { key: 'call_type',     label: 'Call Type',   render: r => r.call_type || '—', toText: r => r.call_type || '' },
  { key: 'status',        label: 'Status',      render: r => r.status || '—', toText: r => r.status || '' },
  { key: 'from_number',   label: 'From',        render: r => r.__from_display || r.visitor_name || '—', toText: r => r.__from_display || r.visitor_name || '' },
  { key: 'unit_number',   label: 'Unit',        render: r => r.unit_number || '—', toText: r => String(r.unit_number || '') },
  { key: 'tenant_name',   label: 'Tenant',      render: r => r.tenant_name || '—', toText: r => r.tenant_name || '' },
  { key: 'duration',      label: 'Duration',    render: r => fmtDuration(r.duration), toText: r => fmtDuration(r.duration) },
  { key: 'door_unlocked', label: 'Unlocked',    render: r => r.door_unlocked ? '✓' : '', toText: r => r.door_unlocked ? 'yes' : 'no' },
  { key: 'door_name',     label: 'Door',        render: r => (r.unlocked_door_names || []).join(', ') || r.door_name || '', toText: r => (r.unlocked_door_names || []).join('; ') || r.door_name || '' },
];

const DEVICE_FIELDS: FieldDef[] = [
  { key: 'occurred_at', label: 'When',     render: r => fmtDateTime(r.occurred_at), toText: r => fmtDateTime(r.occurred_at) },
  { key: 'property_name', label: 'Property', render: r => r.property_name || '—', toText: r => r.property_name || '' },
  { key: 'event_type', label: 'Event',     render: r => r.event_type || '—', toText: r => r.event_type || '' },
  { key: 'method',     label: 'Method',    render: r => r.method || '—', toText: r => r.method || '' },
  { key: 'success',    label: 'Result',    render: r => r.success === false ? 'failed' : 'success', toText: r => r.success === false ? 'failed' : 'success' },
  { key: 'device_name', label: 'Device',    render: r => r.device_name || '—', toText: r => r.device_name || '' },
  { key: 'unit_number', label: 'Unit',     render: r => r.unit_number || '—', toText: r => String(r.unit_number || '') },
  { key: 'person_name', label: 'Person',   render: r => r.person_name || '—', toText: r => r.person_name || '' },
  { key: 'reason',     label: 'Reason',    render: r => r.reason || '', toText: r => r.reason || '' },
];

export const Reports: React.FC = () => {
  const { user } = useAuth();
  const canSeeFullPhone = isAdmin((user?.role as any) || '');
  const [properties, setProperties] = useState<Property[]>([]);
  const [source, setSource] = useState<Source>('calls');
  const [propertyId, setPropertyId] = useState<string>('');
  const [callType, setCallType] = useState<string>('');           // voice | video | ''
  const [eventType, setEventType] = useState<string>('');         // access.door_unlocked | access.door_unlock_attempt | ''
  const [method, setMethod] = useState<string>('');               // dtmf | pin | face | guest_pin | app | ble | remote | ''
  const [unitNumber, setUnitNumber] = useState<string>('');
  const [tenantQ, setTenantQ] = useState<string>('');
  const [unlockedOnly, setUnlockedOnly] = useState<boolean>(false);
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  // PDF-only option — include visitor snapshots that were captured when
  // each video call started. Off by default because PDFs can get large
  // fast (10–20 KB per row when snapshots are inlined as base64).
  const [includeSnapshots, setIncludeSnapshots] = useState<boolean>(false);
  const [pdfSnapshots, setPdfSnapshots] = useState<Record<string, string>>({});
  const [rows, setRows] = useState<any[]>([]);
  const [reportMeta, setReportMeta] = useState<{ totalAvailable: number; totalKnown: boolean; loadedCount: number; truncated: boolean; timezone: string; rowType: string } | null>(null);
  const [ran, setRan] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Property dropdown source.
  useEffect(() => {
    apiList('properties')
      .then((list: any) => {
        const arr = Array.isArray(list) ? list : list?.items ?? [];
        setProperties(arr.map((p: any) => ({ id: p.id, uuid: p.uuid || p.property_uuid, name: p.name })));
      })
      .catch(() => setProperties([]));
  }, []);

  const propNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of properties) m.set(String(p.id), p.name);
    return m;
  }, [properties]);

  const fields = source === 'calls' ? CALL_FIELDS : DEVICE_FIELDS;

  const runReport = async (append = false) => {
    setLoading(true);
    setError(null);
    setRan(true);
    try {
      const qs = new URLSearchParams();
      if (propertyId) qs.set('property_id', propertyId);
      qs.set('limit', '1000');
      qs.set('offset', String(append ? rows.length : 0));
      qs.set('include_meta', '1');
      if (source === 'devices') qs.set('include_vendor', 'true');
      const path = source === 'calls' ? '/events/calls' : '/acs-systems/unlock-events';
      const url = `${process.env.REACT_APP_API_URL || 'https://api.intrlock.io'}${path}?${qs.toString()}`;
      const tok = localStorage.getItem('intrlock_token') || localStorage.getItem('jwt') || localStorage.getItem('token') || '';
      const res = await fetch(url, { headers: tok ? { Authorization: `Bearer ${tok}` } : {} });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const j = await res.json();
      const items = Array.isArray(j) ? j : j.items ?? [];
      // Enrich with property name when missing.
      const enriched = items.map((r: any) => ({
        ...r,
        property_name: r.property_name || propNameById.get(String(r.property_id)) || '',
        __from_display: r.from_number
          ? (canSeeFullPhone ? r.from_number : maskVisitorPhone(r.from_number))
          : '',
      }));
      setRows((previous) => append ? [...previous, ...enriched] : enriched);
      const loadedCount = (append ? rows.length : 0) + enriched.length;
      const totalKnown = j?.total_available !== undefined && j?.total_available !== null;
      const totalAvailable = Number(j?.total_available ?? loadedCount);
      setReportMeta({ totalAvailable, totalKnown, loadedCount, truncated: totalKnown ? loadedCount < totalAvailable : enriched.length >= 1000, timezone: String(j?.timezone || 'UTC'), rowType: String(j?.row_type || (source === 'calls' ? 'calls' : 'unlock events')) });
    } catch (e: any) {
      setError(e?.message || String(e));
      if (!append) { setRows([]); setReportMeta(null); }
    } finally {
      setLoading(false);
    }
  };

  // Client-side filters applied after fetch — same data, multiple slices.
  const filtered = useMemo(() => {
    const fromTs = dateFrom ? new Date(dateFrom).getTime() : 0;
    const toTs = dateTo ? new Date(dateTo + 'T23:59:59').getTime() : 0;
    const unitQ = unitNumber.trim();
    const tQ = tenantQ.trim().toLowerCase();
    return rows.filter((r) => {
      const tsField = source === 'calls' ? r.created_at : r.occurred_at;
      const ts = tsField ? new Date(tsField).getTime() : 0;
      if (fromTs && ts < fromTs) return false;
      if (toTs && ts > toTs) return false;
      if (source === 'calls') {
        if (callType && (r.call_type || '') !== callType) return false;
        if (unlockedOnly && !r.door_unlocked) return false;
        if (unitQ && String(r.unit_number || '') !== unitQ) return false;
        if (tQ) {
          const t = (r.tenant_name || r.visitor_name || '').toLowerCase();
          if (!t.includes(tQ)) return false;
        }
      } else {
        if (eventType && (r.event_type || '') !== eventType) return false;
        if (method && (r.method || '') !== method) return false;
        if (unlockedOnly && r.success === false) return false;
        if (unitQ && String(r.unit_number || '') !== unitQ) return false;
        if (tQ) {
          const p = (r.person_name || '').toLowerCase();
          if (!p.includes(tQ)) return false;
        }
      }
      return true;
    });
  }, [rows, source, callType, eventType, method, unlockedOnly, unitNumber, tenantQ, dateFrom, dateTo]);

  const csvFilename = `${source === 'calls' ? 'call-history' : 'device-history'}-loaded${reportMeta?.truncated ? '-partial' : ''}-${new Date().toISOString().slice(0, 10)}.csv`;

  const exportCsv = () => {
    if (!filtered.length) {
      alert('No rows to export — adjust your filters and run the report again.');
      return;
    }
    // RFC 4180 compliant: quote any cell with comma, quote, CR or LF;
    // double up internal quotes; use CRLF between rows.
    const escape = (v: string) => /["\r\n,]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
    const header = fields.map((f) => escape(f.label)).join(',');
    const lines = filtered.map((r) =>
      fields.map((f) => escape((f.toText ? f.toText(r) : String((r as any)[f.key] ?? '')) || '')).join(',')
    );
    // Prepend UTF-8 BOM so Excel + macOS Numbers recognize the encoding,
    // use CRLF row separators, terminate with a final CRLF.
    const csv = '﻿' + [header, ...lines].join('\r\n') + '\r\n';
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = csvFilename;
    // Safari requires the anchor to be in the DOM before .click() triggers a save.
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 1000);
  };

  // PDF via the browser's native print pipeline. We render a hidden
  // print-only section that contains a clean HTML table of the rows,
  // then trigger window.print(). The user can "Save as PDF" from the
  // print dialog. No third-party PDF lib needed.
  const exportPdf = async () => {
    if (source === 'calls' && includeSnapshots) {
      setLoading(true);
      const API = process.env.REACT_APP_API_URL || 'https://api.intrlock.io';
      const token = localStorage.getItem('intrlock_token') || localStorage.getItem('jwt') || localStorage.getItem('token') || '';
      const candidates = filtered.filter((row: any) => row.call_type === 'video' && row.snapshot_available && row.id).slice(0, 50);
      const entries: [string, string][] = [];
      for (let index = 0; index < candidates.length; index += 5) {
        const batch = await Promise.all(candidates.slice(index, index + 5).map(async (row: any): Promise<[string, string]> => {
          try {
            const response = await fetch(`${API}/events/calls/${encodeURIComponent(String(row.id))}`, {
              headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            if (!response.ok) return [String(row.id), ''];
            const detail = await response.json();
            return [String(row.id), String(detail.snapshot_url || '')];
          } catch { return [String(row.id), '']; }
        }));
        entries.push(...batch);
      }
      setPdfSnapshots(Object.fromEntries(entries));
      setLoading(false);
      requestAnimationFrame(() => requestAnimationFrame(() => window.print()));
      return;
    }
    window.print();
  };

  const inputCls = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-accent-500 focus:border-accent-500';
  const labelCls = 'block text-xs font-medium text-gray-600 mb-1';

  return (
    <div className="shadow-list-page prototype-page reports-page">
      <div className="mb-6 print:hidden">
        <h1 className="text-2xl font-bold text-gray-900">Reports</h1>
        <p className="text-gray-500 mt-1">Filter call and device history, export to CSV or PDF.</p>
      </div>

      <CountBand value={filtered.length} detail={ran ? `Filtered ${reportMeta?.rowType || 'rows'} from ${rows.length} loaded${reportMeta?.totalKnown ? ` of ${reportMeta.totalAvailable} available` : ''}` : "Run a report to populate results"} />
      {ran && reportMeta && <p role="status" className={`mb-3 border px-3 py-2 text-sm ${reportMeta.truncated ? 'border-amber-300 bg-amber-50 text-amber-900' : 'border-gray-300 bg-white text-gray-700'}`}>Source: {reportMeta.rowType} · {reportMeta.loadedCount} loaded{reportMeta.totalKnown ? ` of ${reportMeta.totalAvailable} available` : ' (total unavailable from this API version)'} · All dates from the selected property scope · Timestamps stored in {reportMeta.timezone} and displayed in your local timezone. Date and other filters below apply only to loaded rows. {reportMeta.truncated ? reportMeta.totalKnown ? 'Load more before exporting a complete dataset.' : 'The current API may have truncated the dataset; exports include only loaded rows.' : 'All available rows are loaded.'}</p>}

      <Card className="report-filters mb-6 print:hidden">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3">
          <div>
            <label className={labelCls}>Source</label>
            <select value={source} onChange={(e) => { setSource(e.target.value as Source); setRows([]); setReportMeta(null); setRan(false); }} className={inputCls}>
              <option value="calls">Call Events</option>
              <option value="devices">Device Events</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Property</label>
            <select value={propertyId} onChange={(e) => { setPropertyId(e.target.value); setRows([]); setReportMeta(null); setRan(false); }} className={inputCls}>
              <option value="">All properties</option>
              {properties.map((p) => <option key={String(p.id)} value={String(p.id)}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Date From</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Date To</label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className={inputCls} />
          </div>

          {source === 'calls' ? (
            <>
              <div>
                <label className={labelCls}>Call Type</label>
                <select value={callType} onChange={(e) => setCallType(e.target.value)} className={inputCls}>
                  <option value="">Any</option>
                  <option value="voice">Voice</option>
                  <option value="video">Video</option>
                </select>
              </div>
              <div>
                <label className={labelCls}>Unit Number</label>
                <input type="text" value={unitNumber} onChange={(e) => setUnitNumber(e.target.value)} placeholder="e.g. 101" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Tenant / Visitor</label>
                <input type="text" value={tenantQ} onChange={(e) => setTenantQ(e.target.value)} placeholder="search name" className={inputCls} />
              </div>
              <div className="flex items-end pb-1">
                <label className="flex items-center gap-2 cursor-pointer text-sm">
                  <input type="checkbox" checked={unlockedOnly} onChange={(e) => setUnlockedOnly(e.target.checked)} className="rounded border-gray-300 text-accent-600 focus:ring-accent-500 h-4 w-4" />
                  Door-unlocked only
                </label>
              </div>
            </>
          ) : (
            <>
              <div>
                <label className={labelCls}>Event Type</label>
                <select value={eventType} onChange={(e) => setEventType(e.target.value)} className={inputCls}>
                  <option value="">Any</option>
                  <option value="access.door_unlocked">Door unlocked</option>
                  <option value="access.door_unlock_attempt">Unlock attempt</option>
                </select>
              </div>
              <div>
                <label className={labelCls}>Method</label>
                <select value={method} onChange={(e) => setMethod(e.target.value)} className={inputCls}>
                  <option value="">Any</option>
                  <option value="dtmf">DTMF</option>
                  <option value="pin">PIN</option>
                  <option value="face">Face</option>
                  <option value="face_recognition">Face (legacy)</option>
                  <option value="guest_pin">Guest PIN</option>
                  <option value="app">Mobile App</option>
                  <option value="remote">Remote</option>
                  <option value="ble">Bluetooth</option>
                </select>
              </div>
              <div>
                <label className={labelCls}>Unit Number</label>
                <input type="text" value={unitNumber} onChange={(e) => setUnitNumber(e.target.value)} placeholder="e.g. 101" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Person</label>
                <input type="text" value={tenantQ} onChange={(e) => setTenantQ(e.target.value)} placeholder="search name" className={inputCls} />
              </div>
              <div className="flex items-end pb-1">
                <label className="flex items-center gap-2 cursor-pointer text-sm">
                  <input type="checkbox" checked={unlockedOnly} onChange={(e) => setUnlockedOnly(e.target.checked)} className="rounded border-gray-300 text-accent-600 focus:ring-accent-500 h-4 w-4" />
                  Successful only
                </label>
              </div>
            </>
          )}
        </div>

        {/* PDF-only options — appear above the action row so it's clear they
            only affect the PDF export, not CSV or the on-screen table. */}
        {source === 'calls' && (
          <div className="mt-4 pt-3 border-t border-gray-100 flex flex-wrap items-center gap-4 text-sm">
            <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">PDF options</span>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={includeSnapshots} onChange={(e) => setIncludeSnapshots(e.target.checked)} className="rounded border-gray-300 text-accent-600 focus:ring-accent-500 h-4 w-4" />
              Include video-caller snapshots
            </label>
            <span className="text-[11px] text-gray-400">Only video calls with captured snapshots will show an image.</span>
          </div>
        )}

        <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-gray-100">
          <Button onClick={() => runReport()} disabled={loading} className="bg-accent-500 hover:bg-accent-600">
            {loading ? 'Running…' : 'Run Report'}
          </Button>
          {reportMeta?.truncated && reportMeta.totalKnown && <Button variant="secondary" onClick={() => runReport(true)} disabled={loading}>Load next {reportMeta.loadedCount.toLocaleString()}</Button>}
          <Button variant="secondary" onClick={exportCsv} disabled={filtered.length === 0}>Export loaded CSV</Button>
          <Button variant="secondary" onClick={exportPdf} disabled={filtered.length === 0}>Export loaded PDF</Button>
          {ran && (
            <span className="text-xs text-gray-500 self-center ml-auto">
              {filtered.length} {filtered.length === 1 ? 'row' : 'rows'}{filtered.length !== rows.length ? ` (of ${rows.length} fetched)` : ''}
            </span>
          )}
        </div>
        {error && <p className="text-xs text-red-600 mt-2">{error}</p>}
      </Card>

      {/* Print-only visitor-snapshot gallery. The list API omits large inline
          images; PDF export loads at most 50 selected images via scoped call
          detail requests before opening the print dialog. */}
      {includeSnapshots && source === 'calls' && (
        <div className="hidden print:block mb-6 break-inside-avoid">
          <h3 className="text-base font-semibold text-gray-900 mb-2">Visitor Snapshots</h3>
          <div className="grid grid-cols-3 gap-3">
            {filtered
              .filter((r: any) => r.call_type === 'video' && (pdfSnapshots[String(r.id)] || r.snapshot_url) && String(pdfSnapshots[String(r.id)] || r.snapshot_url).startsWith('data:image/'))
              .map((r: any, i: number) => (
                <div key={r.id || i} className="border border-gray-200 rounded p-2 break-inside-avoid">
                  <img src={pdfSnapshots[String(r.id)] || r.snapshot_url} alt="Visitor" className="w-full h-auto rounded" />
                  <div className="mt-1 text-[10px] text-gray-700">
                    <div>{fmtDateTime(r.created_at)}</div>
                    <div>{r.property_name || ''}{r.unit_number ? ` · Unit ${r.unit_number}` : ''}</div>
                    <div>{r.tenant_name || r.visitor_name || r.__from_display || ''}</div>
                  </div>
                </div>
              ))}
            {filtered.filter((r: any) => r.call_type === 'video' && (pdfSnapshots[String(r.id)] || r.snapshot_url)).length === 0 && (
              <div className="col-span-3 text-xs text-gray-500 italic">No visitor snapshots in this report.</div>
            )}
          </div>
        </div>
      )}

      <Card padding={false} className="print:shadow-none print:border-0">
        <div className="hidden print:block p-4 border-b border-gray-200">
          <h2 className="text-lg font-bold">Intrlock — {source === 'calls' ? 'Call Events' : 'Device Events'} Report</h2>
          <p className="text-xs text-gray-500">Generated {new Date().toLocaleString()} · {filtered.length} loaded rows{reportMeta?.totalKnown ? ` of ${reportMeta.totalAvailable} available` : ''}{reportMeta?.truncated ? ' · PARTIAL DATASET' : ''}</p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {fields.map((f) => (
                  <th key={f.key} className="px-3 py-2 text-left font-semibold text-gray-600 whitespace-nowrap">{f.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={fields.length} className="px-3 py-8 text-center text-gray-400">
                    {ran ? 'No rows match these filters.' : 'Run a report to see results.'}
                  </td>
                </tr>
              ) : filtered.map((r, idx) => (
                <tr key={r.id || idx} className="border-b border-gray-100 hover:bg-gray-50">
                  {fields.map((f) => (
                    <td key={f.key} className="px-3 py-2 align-top text-gray-700 whitespace-nowrap">
                      {f.render ? f.render(r) : ((r as any)[f.key] ?? '—')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Print-only styles — hide chrome, ensure full-width table. */}
      <style>{`
        @media print {
          body { background: white; }
          .print\\:hidden { display: none !important; }
          .print\\:block { display: block !important; }
          .print\\:shadow-none { box-shadow: none !important; }
          .print\\:border-0 { border: 0 !important; }
        }
      `}</style>
    </div>
  );
};

export default Reports;
